import {
  createAction,
  createSlice,
  nanoid,
  type PayloadAction,
} from '@reduxjs/toolkit';
import _ from 'lodash';

import { CallerSessionStatus } from 'features/caller-session/enums/';
import { parsePhoneNumberWithRemovePlus } from 'features/call/call-base/helpers';
import type {
  ColdHandoffRequest,
  RegularCall,
  TeamingRequest,
} from 'features/rns/interfaces';
import type { HearingParticipantSyncUpdate } from 'features/call/call-hearing/interfaces';

//
// Types and values
//

export const NoSessionId = null;

interface CallPushPayload {
  caller: string;
  relayCallId: string;
  isDeafToHearing: boolean;
  endpointMacAddress: string;
}

//
// Slice definition
//

export interface CallerSessionState {
  id: string | null;
  status: CallerSessionStatus;
  caller: string;
  relayCallId: string;
  webRtcRemoteIdentities: string[];
  switchboardConnections: string[];
  errorMessages: string[];
  endpointMacAddress: string;
  isWaitingForCalls: boolean;
}

export const initialCallerSessionState: CallerSessionState = {
  id: NoSessionId,
  status: CallerSessionStatus.NotTakingCalls,
  caller: '',
  relayCallId: '',
  webRtcRemoteIdentities: [] as string[],
  switchboardConnections: [] as string[],
  errorMessages: [] as string[],
  endpointMacAddress: '',
  isWaitingForCalls: false,
};

const callerSessionSlice = createSlice({
  name: 'callerSession',
  initialState: initialCallerSessionState,

  reducers: {
    startedTakingCalls: (state) => {
      if (state.status !== CallerSessionStatus.NotTakingCalls) {
        return;
      }
      state.status = CallerSessionStatus.TakingCalls;
      state.isWaitingForCalls = true;
    },

    stoppedTakingCalls: (state) => {
      // taking calls and considering can both stop taking calls potentially in case of an error.
      if (
        state.status === CallerSessionStatus.TakingCalls ||
        state.status === CallerSessionStatus.ConsideringPushedCall
      ) {
        state.status = CallerSessionStatus.NotTakingCalls;
      } else {
        state.errorMessages.push(
          'Stopped taking calls while in status ' + state.status
        );
      }
      state.isWaitingForCalls = false;
    },

    setWaitingForCalls: (state, { payload }: PayloadAction<boolean>) => {
      state.isWaitingForCalls = payload;
    },

    /** Declining a call also takes us out of the call queue. */
    declinedPushedCall: (state) => {
      if (state.status === CallerSessionStatus.ConsideringPushedCall) {
        return initialCallerSessionState;
      }
    },

    missedPushedCall: (state) => {
      if (state.status === CallerSessionStatus.ConsideringPushedCall) {
        return initialCallerSessionState;
      }
    },

    newRtcConnection: (state, { payload }: PayloadAction<string>) => {
      if (state.status !== CallerSessionStatus.InCallerSession) {
        state.errorMessages.push('new connection while not in caller session');
        return;
      }
      state.webRtcRemoteIdentities.push(payload);
    },
    newHearingConnection: (state, { payload }: PayloadAction<string>) => {
      if (state.status !== CallerSessionStatus.InCallerSession) {
        state.errorMessages.push('new connection while not in caller session');
        return;
      }

      const parsedNumber = parsePhoneNumberWithRemovePlus(payload);
      if (!state.switchboardConnections.includes(parsedNumber)) {
        state.switchboardConnections.push(parsedNumber);
      }
    },

    rtcDisconnection: (state, { payload }: PayloadAction<string>) => {
      if (state.status === CallerSessionStatus.InCallerSession) {
        if (!payload) {
          return;
        }

        // if there are multiple only remove one.
        const idx = state.webRtcRemoteIdentities.findIndex(
          (c) => c === payload
        );
        if (idx !== -1) {
          state.webRtcRemoteIdentities.splice(idx, 1);
        }
      }
    },
    rtcRefer: (
      state,
      { payload }: PayloadAction<{ oldConnection: string }>
    ) => {
      state.webRtcRemoteIdentities = state.webRtcRemoteIdentities.filter(
        (c) => c !== payload.oldConnection
      );
    },
    hearingDisconnection: (state, { payload }: PayloadAction<string>) => {
      const normalizedNumber = parsePhoneNumberWithRemovePlus(payload);
      state.switchboardConnections = state.switchboardConnections?.filter(
        (c) => c !== normalizedNumber
      );
    },
    callerSessionEnded: (state) => {
      if (state.status !== CallerSessionStatus.InCallerSession) {
        return;
      }
      if (
        _.isEmpty(state.webRtcRemoteIdentities) &&
        _.isEmpty(state.switchboardConnections)
      ) {
        // Already cleaned up!
        state.status = CallerSessionStatus.NotTakingCalls;

        state.caller = '';
        state.errorMessages = [];
        state.id = '';
        state.relayCallId = '';
      } else {
        state.errorMessages.push(
          'Caller session tried to end with active connections: ' +
            state.webRtcRemoteIdentities.join(', ') +
            ' and ' +
            state.switchboardConnections.join(', ')
        );
      }
    },
    resetCallerSession: (state) => {
      // If we're waiting for calls, preserve that state to prevent
      // race conditions with reset operations
      if (state.isWaitingForCalls) {
        return {
          ...initialCallerSessionState,
          isWaitingForCalls: true,
          status: CallerSessionStatus.TakingCalls,
        };
      }
      return initialCallerSessionState;
    },
    /** The VI disconnected from the voice session so no switchboard connections are left. */
    voiceSessionEnded: (state) => {
      state.switchboardConnections = [];
    },
    updateSwitchboardConnectionsOnSync: (
      state,
      { payload }: PayloadAction<HearingParticipantSyncUpdate[]>
    ) => {
      state.switchboardConnections = payload
        .filter(({ status }) => ['connected', 'connecting'].includes(status))
        .map(({ phoneNumber }) => parsePhoneNumberWithRemovePlus(phoneNumber));
    },
  },

  extraReducers: (builder) => {
    builder.addCase(
      incomingCallPushed,
      (state, action: PayloadAction<CallPushPayload>) => {
        if (state.status === CallerSessionStatus.TakingCalls) {
          state.status = CallerSessionStatus.ConsideringPushedCall;
          state.relayCallId = action.payload.relayCallId;
          state.endpointMacAddress = action.payload.endpointMacAddress;
        } else {
          state.errorMessages.push(
            'Call pushed while in status ' + state.status
          );
        }
      }
    );

    // This is what will begin a caller session.
    builder.addCase(incomingCallAccepted, (state, action) => {
      if (state.status !== CallerSessionStatus.ConsideringPushedCall) {
        state.errorMessages.push(
          'Cannot accept a call while in status ' + state.status
        );
        return;
      }

      if (state.relayCallId !== action.payload.relayCallId) {
        const expected = state.relayCallId;
        const received = action.payload.relayCallId;
        state.errorMessages.push(
          `Cannot accept a call with unexpected Relay call ID. Expected: ${expected}, Received: ${received}`
        );
        return;
      }

      state.status = CallerSessionStatus.InCallerSession;
      state.id = action.payload.newCallerSessionId;
      state.relayCallId = action.payload.relayCallId;
      state.caller = action.payload.callerPhoneNumber;
    });

    builder.addMatcher(
      () => true,
      () => {
        // TODO: This reducer will run after every action,
        // and will make sure that the caller session ends
        // when everyone becomes disconnected.
        // Edge case: Zoom/Teams calls might not appear to have anyone connected
        // but they do still represent a caller session.
      }
    );
  },
});

export const callerSessionReducer = callerSessionSlice.reducer;

//
// Actions
//

export const {
  startedTakingCalls,
  stoppedTakingCalls,
  setWaitingForCalls,
  declinedPushedCall,
  missedPushedCall,
  newRtcConnection,
  rtcDisconnection,
  newHearingConnection,
  hearingDisconnection,
  callerSessionEnded,
  resetCallerSession,
  rtcRefer,
  voiceSessionEnded,
  updateSwitchboardConnectionsOnSync,
} = callerSessionSlice.actions;

export const incomingCallPushed = createAction(
  'callerSession/incomingCallPushed',
  (data: RegularCall | ColdHandoffRequest) => {
    const isRegularCall = (obj: any): obj is RegularCall => {
      return obj.Call !== undefined;
    };
    const callProperty = isRegularCall(data) ? data.Call : data.HandoffCall;

    return {
      payload: {
        caller: callProperty.CallerConnectionString,
        relayCallId: callProperty.Id.toString(),
        isDeafToHearing: callProperty.IsDeafToHearing,
        endpointMacAddress: callProperty.EndpointMacAddress,
      },
    };
  }
);

export const teamingCallPushed = createAction(
  'callerSession/incomingCallPushed',
  (data: TeamingRequest) => {
    return {
      payload: {
        caller: data.TeamingCall.CallerConnectionString,
        relayCallId: data.TeamingCall.Id.toString(),
        isDeafToHearing: data.TeamingCall.IsDeafToHearing,
        endpointMacAddress: data.TeamingCall.EndpointMacAddress,
      },
    };
  }
);

// This means an incoming call has been accepted by the interpeter.
// This will begin a caller session.
export const incomingCallAccepted = createAction(
  'callerSession/incomingCallAccepted',
  (relayCallId: string, callerPhoneNumber: string) => {
    return {
      payload: {
        relayCallId,
        callerPhoneNumber,
        newCallerSessionId: nanoid(),
      },
    };
  }
);
